/**
 * Canonical URL Builder
 * Single source of truth for building domain URLs with entry routes
 * 
 * URL Structure:
 * - Platform:      https://vidhyantra.local/platform/dashboard (local dev apex)
 *                  https://platform.vidhyantra.com/platform/dashboard (staging/prod)
 * - Organization:  https://acme.vidhyantra.local/org/dashboard
 * - Branch:        https://acme.vidhyantra.local/main-campus/branch/dashboard
 *                  ↑ orgSlug in subdomain    ↑ branchSlug in path
 * 
 * PHASE-1 SAFE: No context selection, only URL construction
 */

import { UrlGenerator } from './url-generator.util'
import { getEntryRouteForContext } from '@/config/entry-routes.config'
import type { UserContext } from './context.util'

/**
 * Build canonical URL for a context
 * Combines UrlGenerator (domain logic) + getEntryRouteForContext (route logic)
 * 
 * Examples:
 * - Platform:      https://platform.vidhyantra.local/platform/dashboard
 * - Organization:  https://acme.vidhyantra.local/org/dashboard
 * - Branch:        https://acme.vidhyantra.local/main-campus/branch/dashboard
 * 
 * @param ctx - User context
 * @returns Canonical URL with entry route
 * @throws Error if context is invalid or missing required fields
 */
export function buildCanonicalUrl(ctx: UserContext): string {
  // Validate context
  if (!ctx || !ctx.type) {
    throw new Error('Invalid context: missing type')
  }
  // Support both orgSlug (context.util) and organizationSlug (auth.types/API)
  const orgSlug = ctx.orgSlug ?? (ctx as { organizationSlug?: string }).organizationSlug
  const branchSlug = ctx.branchSlug

  // Get canonical entry route (e.g., /branch/dashboard)
  const entryRoute = getEntryRouteForContext(
    ctx.type.toLowerCase() as 'platform' | 'organization' | 'branch',
    orgSlug,
    branchSlug
  )

  // Build URL based on context type
  switch (ctx.type) {
    case 'PLATFORM':
      // Local dev: https://vidhyantra.local:3000/platform/dashboard (apex; no platform.* subdomain)
      return UrlGenerator.getPlatformUrl(entryRoute)

    case 'ORGANIZATION':
      if (!orgSlug) {
        throw new Error('Invalid context: organization context requires orgSlug')
      }
      // Result: https://acme.vidhyantra.local/org/dashboard
      return UrlGenerator.getOrganizationUrl(orgSlug, entryRoute)

    case 'BRANCH':
      if (!orgSlug || !branchSlug) {
        throw new Error('Invalid context: branch context requires orgSlug and branchSlug')
      }
      // Result: https://acme.vidhyantra.local/main-campus/branch/dashboard
      // UrlGenerator.getBranchUrl automatically adds branchSlug to path
      return UrlGenerator.getBranchUrl(orgSlug, branchSlug, entryRoute)

    default:
      throw new Error(`Unknown context type: ${ctx.type}`)
  }
}

/**
 * Validate context has required fields for URL building
 * 
 * @param ctx - User context
 * @returns true if valid, false otherwise
 */
export function isValidContextForUrl(ctx: UserContext): boolean {
  if (!ctx || !ctx.type) {
    return false
  }
  const orgSlug = ctx.orgSlug ?? (ctx as { organizationSlug?: string }).organizationSlug
  switch (ctx.type) {
    case 'PLATFORM':
      return true

    case 'ORGANIZATION':
      return !!orgSlug

    case 'BRANCH':
      return !!orgSlug && !!ctx.branchSlug

    default:
      return false
  }
}

